import React, { useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

export interface StripeSetupFormProps {
  clientSecret: string;
  publishableKey: string;
  onSuccess: () => void;
  onCancel: () => void;
  submitLabel?: string;
  /** Optional billing details to pass to Stripe when confirming setup. */
  billingDetails?: { name?: string; email?: string; phone?: string };
}

const stripePromiseCache: Record<string, Promise<Stripe | null>> = {};

/**
 * Keep setup modal compact: card-only UI and no extra contact fields.
 * Billing contact data is handled via existing TMOS profile + Stripe customer.
 */
const paymentElementOptions = {
  layout: 'tabs' as const,
  paymentMethodOrder: ['card'],
  fields: {
    billingDetails: {
      name: 'never' as const,
      email: 'never' as const,
      // We don't always have a phone/address available to pass into confirmSetup.
      // If we opt out ('never'), Stripe requires us to provide it in confirmParams; use 'auto' to avoid blocking.
      phone: 'auto' as const,
      address: 'auto' as const,
    },
  },
};

function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  if (!stripePromiseCache[publishableKey]) {
    stripePromiseCache[publishableKey] = loadStripe(publishableKey);
  }
  return stripePromiseCache[publishableKey];
}

function SetupFormInner({
  onSuccess,
  onCancel,
  submitLabel = 'Save card',
  billingDetails,
}: {
  onSuccess: () => void;
  onCancel: () => void;
  submitLabel?: string;
  billingDetails?: { name?: string; email?: string; phone?: string };
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setErrorMessage(null);
    setIsProcessing(true);
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/tenant-configuration/billing-card?card=complete`,
          payment_method_data: {
            billing_details: {
              ...(billingDetails?.name ? { name: billingDetails.name } : {}),
              ...(billingDetails?.email ? { email: billingDetails.email } : {}),
              ...(billingDetails?.phone ? { phone: billingDetails.phone } : {}),
            },
          },
        },
        redirect: 'if_required',
      });
      if (error) {
        setErrorMessage(error.message ?? 'Card setup failed');
        setIsProcessing(false);
        return;
      }
      onSuccess();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Card setup failed');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={paymentElementOptions} />
      {errorMessage && (
        <p className="text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-[var(--color-border)] rounded-md text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || !elements || isProcessing}
          className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:bg-[var(--color-primary-bg)]/90 disabled:opacity-50"
        >
          {isProcessing ? 'Processing…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

export const StripeSetupForm: React.FC<StripeSetupFormProps> = ({
  clientSecret,
  publishableKey,
  onSuccess,
  onCancel,
  submitLabel,
  billingDetails,
}) => {
  const stripePromise = getStripePromise(publishableKey);

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#1a5d2e',
            borderRadius: '6px',
          },
        },
      }}
    >
      <SetupFormInner onSuccess={onSuccess} onCancel={onCancel} submitLabel={submitLabel} billingDetails={billingDetails} />
    </Elements>
  );
};

